import { useState } from "react";
import { GetNewReqOrderingProvidersForLimsId } from "../NewRequisition/Services/NewReqService";
import type {
  NewReqOrderingProvider,
  OrderingProviderLookupError,
} from "../NewRequisition/types";

export interface ResolvedAccount {
  limsId: string;
  name: string;
  isMobilePhlebotomyAccount: boolean;
  providers: NewReqOrderingProvider[];
}

export interface MslAccountLookup {
  accountNumberInput: string;
  setAccountNumberInput: (value: string) => void;
  resolvedAccount: ResolvedAccount | null;
  isLookingUp: boolean;
  lookupError: OrderingProviderLookupError | undefined;
  lookupAccount: () => Promise<ResolvedAccount | null>;
  clearAccount: () => void;
}

/**
 * Owns the MSL "order on behalf of another account" lookup: the typed account
 * number, in-flight/error state, and the resolved account.
 */
export const useMslAccountLookup = (
  authToken: string | undefined,
  setLoggedOut: (inactivity: boolean) => void,
): MslAccountLookup => {
  const [accountNumberInput, setAccountNumberInputValue] = useState("");
  const [resolvedAccount, setResolvedAccount] =
    useState<ResolvedAccount | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState<
    OrderingProviderLookupError | undefined
  >();

  // Editing the account number invalidates any stale lookup error.
  const setAccountNumberInput = (value: string) => {
    setAccountNumberInputValue(value);
    setLookupError(undefined);
  };

  const lookupAccount = async (): Promise<ResolvedAccount | null> => {
    const enteredLimsId = accountNumberInput.trim();
    if (!enteredLimsId || !authToken) return null;

    setIsLookingUp(true);
    setLookupError(undefined);
    const result = await GetNewReqOrderingProvidersForLimsId(
      enteredLimsId,
      authToken,
      setLoggedOut,
    );
    setIsLookingUp(false);

    if (result.error) {
      setLookupError(result.error);
      setResolvedAccount(null);
      return null;
    }

    const account: ResolvedAccount = {
      limsId: result.limsId,
      name: result.accountName,
      isMobilePhlebotomyAccount: result.isMobilePhlebotomyAccount,
      providers: result.providers,
    };
    setResolvedAccount(account);
    return account;
  };

  const clearAccount = () => {
    setAccountNumberInputValue("");
    setResolvedAccount(null);
    setLookupError(undefined);
  };

  return {
    accountNumberInput,
    setAccountNumberInput,
    resolvedAccount,
    isLookingUp,
    lookupError,
    lookupAccount,
    clearAccount,
  };
};
